import React, { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { useApiClient } from "../../contexts/ApiClientContext";
import { useAuth } from "../../contexts/AuthContext";
import SETTINGS from "../../globals";
import UserRoles from "../../model/enums/UserRole";
import PagedData from "../../model/PagedData";
import APIEndPoints from "../../utils/APIEndPoints";
import AppIcons from "../../utils/AppIcons";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import Loading from "../common/Loading";
import CollectionsListElement from "./CollectionsListElement";
import Collection from "../../model/Collection";

function CollectionsList() {
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the navigator
  const navigate = useNavigate();
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // activities information to be displayed
  const [data, setData] = useState<Collection[]>([]);
  // manage data loading animation
  const [loading, setLoading] = useState(true);

  // load record data from the server
  useEffect(() => {
    apiClient
      ?.get<PagedData<Collection[]>>({
        url: APIEndPoints.COLLECTIONS.ALL,
        requiresToken: true,
      })
      .then((data: PagedData<Collection[]>) => {
        setData(data.items);
      })
      .catch(() => {
        navigate(SETTINGS.get_path({ section: AppSections.SIGN_IN }));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [apiClient, navigate]);

  if (loading) {
    return <Loading />;
  }

  const canEdit =
    !EmbeddingToolInfo.isChemiTool() &&
    (authManager.isAdmin() ||
      authManager.isModerator() ||
      authManager.isSpecialist());
  
  return (
    <div>
      <div className="list-page-header">
        <div className="list-page-title">
          <h1>
            <span>{AppIcons.COLLECTION}</span>
            Collections
            <span className="tooltip-hint">
              <a data-tooltip-id="form-tooltip" data-tooltip-place="right">
                {AppIcons.INFO}
              </a>
              <Tooltip id="form-tooltip">
                Refers to a set of objects which an individual or an
                <br />
                establishment has assembled, classiﬁed, selected, and
                <br />
                preserved in a safe setting and usually displays to a smaller
                <br />
                or larger audience, according to whether the collection is
                <br />
                public or private.
              </Tooltip>
            </span>
          </h1>
        </div>
        <div>
          {canEdit && (
            <Button
              label={"Create a new Collection"}
              purpose={ButtonAction.CREATE}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.COLLECTIONS,
                    subsection: AppSubSections.CREATE,
                  }),
                )
              }
            />
          )}
        </div>
      </div>
      {data?.length === 0 && (
        <div className="list-no-records">
          No records found. Change search parameters and try again.
        </div>
      )}
      {data?.length > 0 &&
        data.map((item) => {
          // check EDIT rights
          const canEdit =
            !EmbeddingToolInfo.isChemiTool() &&
            (item.id_user === authManager.getUserID() ||
              authManager.getUserRole() === UserRoles.ADMIN);

          return (
            <CollectionsListElement
              canEdit={canEdit}
              data={item}
              key={item.id}
            />
          );
        })}
    </div>
  );
}

export default CollectionsList;
